import Checkbox from '@mui/material/Checkbox'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'

import './table.scss'

import 'components/molecules/personalCampaignsStatistics/style.scss'
import selectedCheckboxIcon from 'assets/image/icons/tables/checkbox2.svg'
import notSelectedCheckboxIcon from 'assets/image/icons/tables/checkbox.svg'
import clsx from 'clsx'
import { PaginationWrapper } from 'components/atoms/mTablePagination/PaginationWrapper'
import { InformationalTip } from 'components/molecules/InformationalTip/InformationalTip'
import { CONSTS } from 'config/objectConst'

import type { Campaign } from '../../types'

import { Row } from '../Row/Row'
import { TableHint } from '../TableHint/TableHint'
import { TableLoader } from '../TableLoader/TableLoader'
import { LastHeaders } from './LastHeaders'
import { SelectHeader } from './SelectHeader'
import tableCSS from './Table.module.css'
import { useHasScrollbar } from './useHasScrollbar'
import { useSelect } from './useSelect'

const archiveGetClientWidth = (name: string) =>
	(name.length > 25 &&
		document.documentElement.clientWidth > 1000 &&
		document.documentElement.clientWidth < 1530) ||
	(name.length > 30 &&
		document.documentElement.clientWidth > 1530 &&
		document.documentElement.clientWidth < 1740) ||
	(name.length > 35 &&
		document.documentElement.clientWidth > 1744 &&
		document.documentElement.clientWidth < 1918) ||
	(name.length > 42 &&
		document.documentElement.clientWidth > 1918 &&
		document.documentElement.clientWidth < 2133) ||
	(name.length > 49 &&
		document.documentElement.clientWidth > 2132 &&
		document.documentElement.clientWidth < 2401)

const getDataClientWidth = (name: string) =>
	(name?.length > 30 &&
		document.documentElement.clientWidth > 1000 &&
		document.documentElement.clientWidth < 1537) ||
	(name?.length > 45 &&
		document.documentElement.clientWidth > 1537 &&
		document.documentElement.clientWidth < 1746)

interface Props {
	archive?: boolean
	campaigns: Campaign[]
	page: number
	setPage: (page: number) => void

	total: number
}

export function TableCampaigns({
	archive = false,
	campaigns,
	page,
	setPage,
	total
}: Props) {
	const { hasScrollbar, ref } = useHasScrollbar()
	const {
		areAllSelected,
		clearAll,
		handleChangeCheckbox,
		handleRowCheck,
		handleSelectAll,
		removeId,
		selected
	} = useSelect({ items: campaigns, total })

	const rowsPerPage = CONSTS.PAGE_SIZE
	const hasPagination = total > rowsPerPage

	return (
		<div
			className={clsx(
				'table-container campaigns-container',
				hasPagination && 'table-with-footer',
				tableCSS.root
			)}
		>
			<div className='hint-tooltip-wrapper'>
				<TableContainer className='table-body campaigns  with-checkboxs'>
					<TableLoader />
					<Table
						aria-label='a dense table'
						className={'table'}
						size='small'
						style={{ tableLayout: 'auto' }}
					>
						<TableHead>
							<TableRow>
								<SelectHeader
									areAllSelected={areAllSelected}
									campaigns={campaigns}
									onChangeCheckbox={handleChangeCheckbox}
									selectAll={handleSelectAll}
									selected={selected}
									total={total}
									unSelectAll={clearAll}
								/>
								<LastHeaders hasScroll={hasScrollbar} />
							</TableRow>
						</TableHead>
						<TableBody
							className={clsx(
								'app-scroll',
								hasPagination ? 'table-with-footer' : 'table-with-out-footer'
							)}
							ref={ref}
						>
							{campaigns.map((campaign, index) => (
								<Row id={campaign.id} key={campaign.id}>
									<TableCell
										onClick={event => event.stopPropagation()}
										padding='checkbox'
										style={{ width: '5%' }}
									>
										<div className='checkbox-container'>
											<Checkbox
												checked={areAllSelected || selected.has(campaign.id)}
												checkedIcon={
													<img alt='checkbox' src={selectedCheckboxIcon} />
												}
												icon={
													<img alt='checkbox' src={notSelectedCheckboxIcon} />
												}
												onClick={() => handleRowCheck(campaign.id)}
												slotProps={{
													input: {
														'aria-labelledby': `enhanced-table-checkbox-${index}`
													}
												}}
											/>
										</div>
									</TableCell>

									<Row.NameCell
										getDataClientWidth={
											archive ? archiveGetClientWidth : getDataClientWidth
										}
										{...campaign}
									/>

									<Row.StatusCell {...campaign} />

									<Row.CommonCells {...campaign} />

									<Row.LastCells withScroll={hasScrollbar} {...campaign}>
										<Row.Actions
											removeIdFromSelected={removeId}
											{...campaign}
										/>
									</Row.LastCells>
								</Row>
							))}
						</TableBody>
					</Table>
				</TableContainer>
				<TableHint forceHide={!campaigns.length} />
			</div>
			{hasPagination && (
				<PaginationWrapper
					// @ts-ignore
					component='div'
					count={total}
					onPageChange={(_, page) => setPage(page)}
					page={page}
					rowsPerPage={rowsPerPage}
					rowsPerPageOptions={[]}
				/>
			)}

			{!archive && (
				<>
					<Row.Actions.Modal />
					<InformationalTip />
				</>
			)}
		</div>
	)
}
